import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getMe, changePassword } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Home,
  Calendar,
  BookOpen,
  Users,
  MessageSquare,
  AlertTriangle,
  FileText,
  Settings,
  LogOut,
  ChevronRight,
  Wrench,
  UserCheck,
  RefreshCw,
  Mail,
  KeyRound,
  X
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_webapp-deploy-2/artifacts/8scrf17e_logo.jpeg";

const sidebarLinks = [
  { path: "/admin", label: "Tableau de bord", icon: LayoutDashboard },
  { path: "/admin/logements", label: "Logements", icon: Home },
  { path: "/admin/calendrier", label: "Calendrier", icon: Calendar },
  { path: "/admin/ical-sync", label: "Synchro iCal", icon: RefreshCw },
  { path: "/admin/reservations", label: "Réservations", icon: BookOpen },
  { path: "/admin/prestations", label: "Prestations", icon: Wrench },
  { path: "/admin/proprietaires", label: "Propriétaires", icon: UserCheck },
  { path: "/admin/leads", label: "Leads", icon: Users },
  { path: "/admin/incidents", label: "Incidents", icon: AlertTriangle },
  { path: "/admin/cms", label: "Contenu (CMS)", icon: FileText },
  { path: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { path: "/admin/rapports", label: "Rapports PDF", icon: Settings },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("hh_token");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }
    
    // Validate token by calling /auth/me
    getMe()
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("hh_user", JSON.stringify(res.data));
        setLoading(false);
      })
      .catch(() => {
        // Token invalid/expired — interceptor will handle refresh or redirect
        setLoading(false);
      });
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004333]" />
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem("hh_token");
    localStorage.removeItem("hh_user");
    navigate("/admin/login");
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new !== pwForm.confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setPwLoading(true);
    try {
      await changePassword(pwForm.current, pwForm.new);
      toast.success("Mot de passe modifié avec succès");
      setShowPasswordModal(false);
      setPwForm({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setPwLoading(false);
    }
  };

  const currentPage = sidebarLinks.find(link => link.path === location.pathname);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <Link to="/">
            <img
              src={LOGO_URL}
              alt="Happyhouse conciergerie"
              className="h-12 w-auto brightness-0 invert"
            />
          </Link>
        </div>

        <nav className="py-4">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={isActive ? "active" : ""}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span>{link.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-1">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors text-sm"
            data-testid="change-password-btn"
          >
            <KeyRound className="w-4 h-4" />
            <span>Modifier le mot de passe</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-content">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-display font-semibold text-[#004333]">
                {currentPage?.label || "Administration"}
              </h1>
              <p className="text-sm text-gray-500">
                Bienvenue, {user?.nom || "Admin"}
              </p>
            </div>
            <Link
              to="/"
              className="text-sm text-[#004333] hover:text-[#C9922A] transition-colors"
            >
              Voir le site →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="password-modal">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#004333]">Modifier le mot de passe</h2>
              <button onClick={() => setShowPasswordModal(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel</label>
                <input
                  type="password"
                  required
                  value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none"
                  data-testid="pw-current"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={pwForm.new}
                  onChange={(e) => setPwForm({ ...pwForm, new: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none"
                  data-testid="pw-new"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none"
                  data-testid="pw-confirm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={pwLoading} className="flex-1 px-4 py-2.5 bg-[#004333] text-white rounded-xl font-medium hover:bg-[#003328] transition-colors disabled:opacity-50" data-testid="pw-submit">
                  {pwLoading ? "..." : "Modifier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
